import { useEffect, useState } from 'react';
import { coreAPI } from '../../lib/api';
import { auditsAPI, type AuditPerson } from '../../lib/api/audits';

export interface AuditOption {
  value: string;
  label: string;
}

interface AuditOptions {
  suppliers: AuditOption[];
  divisions: AuditOption[];
  people: AuditOption[];
  loading: boolean;
}

/**
 * Pick-lists for the audit and action dialogs: the customer's approved
 * suppliers and its own divisions (one scoped call, the same one the contract
 * pricing modal uses), and the customer users who can lead an audit or own an
 * action.
 *
 * @param enabled - load only while a dialog is open
 * @returns suppliers, divisions and people as `{ value, label }` rows
 */
export function useAuditOptions(enabled: boolean): AuditOptions {
  const [suppliers, setSuppliers] = useState<AuditOption[]>([]);
  const [divisions, setDivisions] = useState<AuditOption[]>([]);
  const [people, setPeople] = useState<AuditOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || loaded) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([coreAPI.getContractPricingCompanies(), auditsAPI.people()])
      .then(([companies, peopleRes]) => {
        if (cancelled) return;
        const rows = companies.data.results || [];
        const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
        setSuppliers(rows.filter((c) => c.company_type === 'supplier').sort(byName)
          .map((c) => ({ value: String(c.id), label: c.name })));
        setDivisions(rows.filter((c) => c.company_type === 'customer').sort(byName)
          .map((c) => ({ value: String(c.id), label: c.name })));
        setPeople((peopleRes.data as AuditPerson[]).map((p) => ({ value: String(p.id), label: p.name })));
        setLoaded(true);
      })
      .catch((err: unknown) => {
        console.error('Audit options load failed:', err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, loaded]);

  return { suppliers, divisions, people, loading };
}
